// ── SUBRECIPIENTS ────────────────────────────────────────────
// Pass-through entity monitoring: the subrecipient or contractor call
// (2 CFR 200.331), the risk evaluation (200.332(c)) and the monitoring plan
// that follows, the reports each subrecipient owes, and its audits from the
// Federal Audit Clearinghouse.
// Every rating, plan and due date is worked out on the server (lib/subrecipients.js).
import { useState, useEffect, useCallback } from "react";
import { TOKENS } from "../tokens";
import { FONT_H, FONT_UI, FONT_MONO, useT } from "../theme";
import { API, apiFetch } from "../utils/api";

// Same keys and wording as lib/subrecipients.js.
const SUB_TRAITS = {
  a1: "Determines who is eligible to receive what Federal assistance",
  a2: "Has its performance measured against whether the objectives of a Federal program were met",
  a3: "Has responsibility for programmatic decision-making",
  a4: "Is responsible for following the Federal program requirements in the award",
  a5: "Carries out a program for a public purpose, not goods or services for the pass-through entity",
};
const CONTRACTOR_TRAITS = {
  b1: "Provides the goods and services within normal business operations",
  b2: "Provides similar goods or services to many different purchasers",
  b3: "Normally operates in a competitive environment",
  b4: "Provides goods or services that are ancillary to the Federal program",
  b5: "Is not subject to the Federal program compliance requirements because of the agreement",
};
const RISK_FACTORS = {
  experience: ["Prior experience with the same or similar subawards (200.332(c)(1))", { same: "Has managed the same kind of subaward", similar: "Similar subawards only", none: "No prior experience" }],
  audit: ["Results of previous audits (200.332(c)(2))", { clean: "Single Audit with no findings", findings: "Single Audit with findings", none: "No Single Audit on file", serious: "Material weakness, repeat finding or questioned costs" }],
  changes: ["New personnel or new or substantially changed systems (200.332(c)(3))", { neither: "Neither", one: "New key staff or changed systems", both: "Both" }],
  federal: ["Extent and results of Federal agency monitoring (200.332(c)(4))", { clean: "Monitored by a Federal agency, no issues", none: "No Federal agency monitoring", issues: "Federal monitoring found issues" }],
};
const FREQUENCIES = [["monthly", "Monthly"], ["quarterly", "Quarterly"], ["semiannual", "Semiannual"], ["annual", "Annual"]];
const RED = "#dc2626", GREEN = "#059669", AMBER = "#d97706";
const RATING_COLOR = { low: GREEN, medium: AMBER, high: RED };
const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");
const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const newId = () => Math.random().toString(36).slice(2, 12);
const blank = () => ({
  id: newId(), name: "", uei: "", contact: { name: "", email: "" }, awardId: "", subawardNumber: "", amount: 0, start: "", end: "",
  determination: { checks: {}, note: "" },
  risk: { experience: "", audit: "", changes: "", federal: "", auditDetail: { source: "", reportId: "", fyEnd: "", findingCount: 0 }, note: "" },
  reports: [], archived: false,
});
function saveFile(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export default function SubrecipientsPage() {
  const T = useT();
  const [subs, setSubs]       = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [awards, setAwards]   = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError]     = useState("");
  const [busy, setBusy]       = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiFetch(`${API}/subrecipients`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not load subrecipients.");
      setSubs(data.subrecipients || []);
      setOverdue(data.overdue || []);
    } catch (e) { setError(e.message); }
  }, []);
  useEffect(() => {
    load();
    apiFetch(`${API}/setup/awards`).then((r) => r.json()).then((d) => setAwards(d.records || [])).catch(() => {});
  }, [load]);

  const download = async (path, name) => {
    setBusy(true); setError("");
    try {
      const res = await apiFetch(`${API}/subrecipients/${path}`);
      if (!res.ok) throw new Error((await res.json()).error || "Could not build the workbook.");
      saveFile(await res.blob(), name);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  // Saving keeps the editor open, so the rating and plan the server worked out show straight away.
  const save = async () => {
    setBusy(true); setError("");
    try {
      const { evaluation, updatedAt, ...body } = editing;
      const res = await apiFetch(`${API}/subrecipients/${encodeURIComponent(editing.id)}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not save.");
      setEditing(data.subrecipient);
      await load();
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${editing.name || "this subrecipient"}? This cannot be undone.`)) return;
    setBusy(true); setError("");
    try {
      const res = await apiFetch(`${API}/subrecipients/${encodeURIComponent(editing.id)}`, { method: "DELETE" });
      if (!res.ok) throw new Error((await res.json()).error || "Could not delete.");
      setEditing(null);
      await load();
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const card  = { background: T.bgSurface, border: `1px solid ${T.border}`, borderRadius: 12, padding: 20, marginBottom: 18 };
  const label = { fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.1em", textTransform: "uppercase", color: T.textMuted, marginBottom: 6, display: "block" };
  const input = { width: "100%", padding: "9px 11px", background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: 8, color: T.textBody, fontSize: 13, fontFamily: FONT_UI, outline: "none", boxSizing: "border-box" };
  const btn = (primary) => ({ padding: "10px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: FONT_UI, cursor: "pointer", minHeight: 38, border: `1px solid ${primary ? TOKENS.gold : T.border}`, background: primary ? TOKENS.gold : "transparent", color: primary ? "#fff" : T.textBody });
  const th = { textAlign: "left", padding: "8px 10px", fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: T.textMuted, borderBottom: `1px solid ${T.border}` };
  const td = { padding: "10px", fontFamily: FONT_UI, fontSize: 13, color: T.textBody, borderBottom: `1px solid ${T.border}`, verticalAlign: "top" };
  const ui = { T, card, label, input, btn, th, td };
  const awardName = (id) => { const a = awards.find((x) => x.id === id); return a ? [a.awardNumber, a.projectTitle].filter(Boolean).join(" · ") || a.id : ""; };

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "22px 24px 60px", background: T.bgApp }}>
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <h1 style={{ fontFamily: FONT_H, fontSize: 24, fontWeight: 700, color: T.text, margin: "0 0 4px" }}>Subrecipients</h1>
        <p style={{ fontFamily: FONT_UI, fontSize: 13.5, color: T.textMuted, margin: "0 0 20px" }}>
          For each subaward you make: decide subrecipient or contractor, rate the risk on the four factors in 2 CFR 200.332(c), follow the monitoring plan that comes with the rating, and track the reports the subrecipient owes.
        </p>
        {error && <div role="alert" style={{ ...card, borderColor: RED, color: RED, fontFamily: FONT_UI, fontSize: 13 }}>{error}</div>}

        {editing
          ? <Editor {...ui} s={editing} setS={setEditing} awards={awards} setError={setError} onSave={save} onDelete={remove}
              onCancel={() => setEditing(null)} busy={busy} isNew={!subs.some((x) => x.id === editing.id)}
              onExport={() => download(`${encodeURIComponent(editing.id)}/export`, `risk-assessment-${(editing.name || "subrecipient").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.xlsx`)} />
          : <>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginBottom: 12 }}>
                <button style={btn(false)} onClick={() => download("export", "subrecipients.xlsx")} disabled={busy}>Download list (Excel)</button>
                <button style={btn(true)} onClick={() => setEditing(blank())}>New subrecipient</button>
              </div>
              {overdue.length > 0 && (
                <div style={{ ...card, borderColor: RED }}>
                  <h2 style={{ fontFamily: FONT_H, fontSize: 18, color: RED, margin: "0 0 10px" }}>Overdue reports</h2>
                  <ul style={{ margin: 0, paddingLeft: 18, fontFamily: FONT_UI, fontSize: 13, color: T.textBody }}>
                    {overdue.map((o) => <li key={`${o.subId}:${o.reportId}:${o.periodEnd}`}>{o.name}: {o.label} for the period to {o.periodEnd}, due {o.due} ({o.daysLate} day{o.daysLate === 1 ? "" : "s"} late)</li>)}
                  </ul>
                </div>
              )}
              <SubList {...ui} subs={subs} awardName={awardName} onOpen={setEditing} />
            </>}
      </div>
    </div>
  );
}

function SubList({ T, card, th, td, subs, awardName, onOpen }) {
  if (!subs.length) return <div style={{ ...card, fontFamily: FONT_UI, fontSize: 13.5, color: T.textMuted }}>No subrecipients yet. Add each organization you pass Federal funds to.</div>;
  return (
    <div style={{ ...card, padding: 0, overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>{["Subrecipient", "Award", "Amount", "Determination", "Risk", "Next report due"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
        <tbody>
          {subs.map((s) => {
            const ev = s.evaluation || {};
            const rating = ev.risk?.complete ? ev.risk.rating : "";
            return (
              <tr key={s.id} onClick={() => onOpen(s)} onKeyDown={(e) => e.key === "Enter" && onOpen(s)} tabIndex={0} style={{ cursor: "pointer", opacity: s.archived ? 0.55 : 1 }}>
                <td style={td}><strong>{s.name}</strong><div style={{ color: T.textMuted, fontSize: 12 }}>{s.uei || "No UEI"}{s.subawardNumber && ` · ${s.subawardNumber}`}</div></td>
                <td style={td}>{awardName(s.awardId)}</td>
                <td style={td}>{money(s.amount)}</td>
                <td style={td}>{cap(ev.determination?.result)}</td>
                <td style={{ ...td, color: RATING_COLOR[rating] || T.textMuted, fontWeight: 600 }}>{rating ? cap(rating) : "Incomplete"}</td>
                <td style={td}>
                  {ev.nextDue ? `${ev.nextDue.label}, ${ev.nextDue.due}` : "None"}
                  {ev.overdue > 0 && <span style={{ color: RED, marginLeft: 6 }}>{ev.overdue} overdue</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Editor({ T, card, label, input, btn, th, td, s, setS, awards, setError, onSave, onDelete, onCancel, onExport, busy, isNew }) {
  const [fac, setFac] = useState(null);
  const [facBusy, setFacBusy] = useState(false);
  const set = (k, v) => setS({ ...s, [k]: v });
  const setIn = (k, sub, v) => setS({ ...s, [k]: { ...s[k], [sub]: v } });
  const setCheck = (k, v) => setS({ ...s, determination: { ...s.determination, checks: { ...s.determination.checks, [k]: v } } });
  const setReport = (i, patch) => setS({ ...s, reports: s.reports.map((r, j) => (j === i ? { ...r, ...patch } : r)) });
  const toggleReceived = (reportId, periodEnd, on) => setS({ ...s, reports: s.reports.map((r) => (r.id !== reportId ? r : {
    ...r, received: on ? [...(r.received || []), periodEnd] : (r.received || []).filter((d) => d !== periodEnd),
  })) });
  const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14, marginBottom: 14 };
  const field = (lbl, el) => <label key={lbl} style={{ display: "block" }}><span style={label}>{lbl}</span>{el}</label>;
  const h3 = { fontFamily: FONT_UI, fontSize: 14, color: T.text, margin: "22px 0 10px" };
  const check = (k, text) => (
    <label key={k} style={{ display: "flex", alignItems: "flex-start", gap: 8, fontFamily: FONT_UI, fontSize: 13, color: T.textBody, marginBottom: 6 }}>
      <input type="checkbox" checked={!!s.determination?.checks?.[k]} onChange={(e) => setCheck(k, e.target.checked)} /> {text}
    </label>
  );
  const ev = s.evaluation;
  const ad = s.risk?.auditDetail || {};

  const lookup = async () => {
    setFacBusy(true); setError(""); setFac(null);
    try {
      const res = await apiFetch(`${API}/subrecipients/fac?uei=${encodeURIComponent(s.uei)}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "The audit lookup failed.");
      setFac(data);
    } catch (e) { setError(e.message); }
    setFacBusy(false);
  };
  const useFac = () => setS({ ...s, risk: { ...s.risk, audit: fac.suggestion.audit, auditDetail: fac.suggestion.auditDetail } });

  return (
    <div style={card}>
      <h2 style={{ fontFamily: FONT_H, fontSize: 18, color: T.text, margin: "0 0 16px" }}>{isNew ? "New subrecipient" : s.name}</h2>
      <div style={grid}>
        {field("Legal name (as in SAM.gov)", <input style={input} value={s.name} onChange={(e) => set("name", e.target.value)} />)}
        {field("UEI", <input style={input} value={s.uei} onChange={(e) => set("uei", e.target.value)} placeholder="12 letters and numbers" />)}
        {field("Contact name", <input style={input} value={s.contact?.name || ""} onChange={(e) => setIn("contact", "name", e.target.value)} />)}
        {field("Contact email", <input style={input} type="email" value={s.contact?.email || ""} onChange={(e) => setIn("contact", "email", e.target.value)} />)}
      </div>
      <div style={grid}>
        {field("Federal award (optional)", (
          <select style={input} value={s.awardId} onChange={(e) => set("awardId", e.target.value)}>
            <option value="">None</option>
            {awards.map((a) => <option key={a.id} value={a.id}>{[a.awardNumber, a.projectTitle].filter(Boolean).join(" · ") || a.id}</option>)}
          </select>
        ))}
        {field("Subaward number", <input style={input} value={s.subawardNumber} onChange={(e) => set("subawardNumber", e.target.value)} />)}
        {field("Amount ($)", <input style={input} value={s.amount} onChange={(e) => set("amount", e.target.value)} />)}
        {field("Start", <input style={input} type="date" value={s.start || ""} onChange={(e) => set("start", e.target.value)} />)}
        {field("End", <input style={input} type="date" value={s.end || ""} onChange={(e) => set("end", e.target.value)} />)}
      </div>

      <h3 style={h3}>Subrecipient or contractor (2 CFR 200.331)</h3>
      <p style={{ fontFamily: FONT_UI, fontSize: 12.5, color: T.textMuted, margin: "0 0 10px" }}>Tick what describes this organization. No single characteristic decides it. The substance of the relationship counts more than the form of the agreement.</p>
      <div style={grid}>
        <div><span style={label}>Subrecipient characteristics</span>{Object.entries(SUB_TRAITS).map(([k, t]) => check(k, t))}</div>
        <div><span style={label}>Contractor characteristics</span>{Object.entries(CONTRACTOR_TRAITS).map(([k, t]) => check(k, t))}</div>
      </div>
      {ev && <div style={{ fontFamily: FONT_UI, fontSize: 13, color: ev.determination.result === "subrecipient" ? GREEN : AMBER, marginBottom: 10 }}>
        Result: <strong>{cap(ev.determination.result)}</strong> ({ev.determination.subCount} subrecipient, {ev.determination.conCount} contractor characteristics)
        {ev.determination.result === "contractor" && ". A procurement contract follows 200.317 to 200.327, not subrecipient monitoring."}
      </div>}
      {field("Judgment note", <textarea style={{ ...input, minHeight: 60, resize: "vertical" }} value={s.determination?.note || ""} onChange={(e) => setS({ ...s, determination: { ...s.determination, note: e.target.value } })} />)}

      <h3 style={h3}>Risk assessment (2 CFR 200.332(c))</h3>
      <div style={grid}>
        {Object.entries(RISK_FACTORS).map(([k, [lbl, opts]]) => field(lbl, (
          <select style={input} value={s.risk?.[k] || ""} onChange={(e) => setIn("risk", k, e.target.value)}>
            <option value="">Choose…</option>
            {Object.entries(opts).map(([v, t]) => <option key={v} value={v}>{t}</option>)}
          </select>
        )))}
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
        <button style={btn(false)} onClick={lookup} disabled={facBusy || !s.uei}>{facBusy ? "Looking up…" : "Look up audits in the FAC"}</button>
        <span style={{ fontFamily: FONT_UI, fontSize: 12.5, color: T.textMuted }}>
          {ad.fyEnd ? `Audit answer rests on the fiscal year ending ${ad.fyEnd}, ${ad.findingCount} finding(s)${ad.source ? " (from the FAC)" : ""}.` : "Searches the Federal Audit Clearinghouse by UEI."}
        </span>
      </div>
      {fac && (
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, marginBottom: 14, overflowX: "auto" }}>
          {fac.audits.length ? <>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>{["Fiscal year end", "Accepted", "Federal expenditure", "Auditor", "Findings", "Flags"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
              <tbody>{fac.audits.map((a) => (
                <tr key={a.reportId}>
                  <td style={td}>{a.fyEnd}</td><td style={td}>{a.accepted}</td><td style={td}>{money(a.totalExpended)}</td><td style={td}>{a.auditor}</td><td style={td}>{a.findingCount}</td>
                  <td style={{ ...td, color: RED }}>{[a.repeat && "repeat", a.materialWeakness && "material weakness", a.questionedCosts && "questioned costs"].filter(Boolean).join(", ")}</td>
                </tr>
              ))}</tbody>
            </table>
            <div style={{ padding: 12 }}>
              <button style={btn(true)} onClick={useFac}>Use the latest audit in the risk assessment</button>
            </div>
          </> : <div style={{ padding: 12, fontFamily: FONT_UI, fontSize: 13, color: T.textMuted }}>No Single Audit on file in the FAC for this UEI. It may have spent less than the threshold, or the UEI may be wrong.</div>}
        </div>
      )}
      {field("Risk note", <textarea style={{ ...input, minHeight: 60, resize: "vertical" }} value={s.risk?.note || ""} onChange={(e) => setIn("risk", "note", e.target.value)} />)}

      {ev && (
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, padding: 14, marginTop: 14, fontFamily: FONT_UI, fontSize: 13, color: T.textBody }}>
          {ev.risk.complete
            ? <div style={{ fontSize: 15, marginBottom: 8 }}>Risk rating: <strong style={{ color: RATING_COLOR[ev.risk.rating] }}>{cap(ev.risk.rating)}</strong> ({ev.risk.points} of 8 points)</div>
            : <div style={{ color: AMBER, marginBottom: 8 }}>Answer every risk factor to get the rating. The plan below assumes {ev.risk.rating} risk so far.</div>}
          <div style={{ color: T.textMuted, fontSize: 12, marginBottom: 10 }}>Each factor scores 0 to 2. 0 to 2 points is low, 3 to 4 medium, 5 or more high. Any factor at 2 makes it at least medium.</div>
          <strong>Monitoring plan</strong>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>{ev.plan.steps.map((p) => <li key={p}>{p}</li>)}</ul>
        </div>
      )}

      <h3 style={h3}>Reports the subrecipient owes</h3>
      {s.reports.map((r, i) => (
        <div key={r.id} style={grid}>
          {field("Report", (
            <select style={input} value={r.type} onChange={(e) => setReport(i, { type: e.target.value })}>
              <option value="financial">Financial report</option><option value="performance">Performance report</option>
            </select>
          ))}
          {field("Frequency", (
            <select style={input} value={r.frequency} onChange={(e) => setReport(i, { frequency: e.target.value })}>
              {FREQUENCIES.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
            </select>
          ))}
          {field("Due days after period end", <input style={input} type="number" min="0" value={r.daysAfter} onChange={(e) => setReport(i, { daysAfter: e.target.value })} />)}
          <div style={{ display: "flex", alignItems: "flex-end" }}>
            <button style={btn(false)} onClick={() => setS({ ...s, reports: s.reports.filter((_, j) => j !== i) })}>Remove</button>
          </div>
        </div>
      ))}
      <button style={btn(false)} onClick={() => setS({ ...s, reports: [...s.reports, { id: newId(), type: "financial", frequency: "quarterly", daysAfter: 30, received: [] }] })}>Add report</button>

      {ev && ev.schedule.length > 0 && (
        <div style={{ border: `1px solid ${T.border}`, borderRadius: 10, marginTop: 14, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>{["Report", "Period end", "Due", "Status", "Received"].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>{ev.schedule.map((e) => {
              const rep = s.reports.find((r) => r.id === e.reportId);
              const got = !!rep?.received?.includes(e.periodEnd);
              return (
                <tr key={`${e.reportId}:${e.periodEnd}`}>
                  <td style={td}>{e.label}</td><td style={td}>{e.periodEnd}</td><td style={td}>{e.due}</td>
                  <td style={{ ...td, color: e.received ? GREEN : e.overdue ? RED : T.textBody }}>{e.received ? "Received" : e.overdue ? "Overdue" : "Open"}</td>
                  <td style={td}>{rep && <input type="checkbox" aria-label={`${e.label} for the period to ${e.periodEnd} received`} checked={got} onChange={(x) => toggleReceived(e.reportId, e.periodEnd, x.target.checked)} />}</td>
                </tr>
              );
            })}</tbody>
          </table>
          <div style={{ padding: "8px 10px", fontFamily: FONT_UI, fontSize: 12, color: T.textMuted }}>Save after changing the reports or ticking received to update the dates and alerts.</div>
        </div>
      )}

      <label style={{ display: "flex", alignItems: "center", gap: 8, fontFamily: FONT_UI, fontSize: 13, color: T.textBody, marginTop: 18 }}>
        <input type="checkbox" checked={!!s.archived} onChange={(e) => set("archived", e.target.checked)} /> Subaward closed out (stop alerts and leave off the Excel list)
      </label>

      <div style={{ display: "flex", gap: 10, marginTop: 18, flexWrap: "wrap" }}>
        <button style={btn(true)} onClick={onSave} disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        {!isNew && <button style={btn(false)} onClick={onExport} disabled={busy}>Download risk assessment (Excel)</button>}
        <button style={btn(false)} onClick={onCancel} disabled={busy}>Close</button>
        {!isNew && <button style={{ ...btn(false), color: RED, marginLeft: "auto" }} onClick={onDelete} disabled={busy}>Delete</button>}
      </div>
    </div>
  );
}
